import { closeSuggest } from './defaultSuggest';

const SEARCH_INPUT_SEL = '.mainSearchInput';
const AUTOSUGGEST_SEL = '.autoSuggest';
const ITEM_SEL = '.autoSuggest .suggest-tracking';
const SUGGEST_OPEN_CLASS = 'autoSuggest-container';
const ACTIVE_CLASS = 'suggest-active';

// `currentIndex` is module-global and shared across every `.mainSearchInput`
// (homepage + non-homepage forms). The product UX assumes only one suggest is
// open at a time — switching focus between inputs without closing the suggest
// is not a supported flow. The MutationObserver below also resets the index
// whenever the live suggest re-renders, so any stale value is short-lived.
let currentIndex = -1;
let itemIdCounter = 0;

// Modifier / lock keys the user might tap while a highlight is active without
// the suggest needing to deselect (matches the spec's "any other key" intent
// while excluding modifier-only and lock keys, which would otherwise survive
// the printable-only `event.key.length === 1` check).
const NON_RESETTING_KEYS = new Set([
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'CapsLock',
    'NumLock',
    'ScrollLock',
    'Fn',
    'FnLock',
    'ContextMenu',
]);

const isOpen = (): boolean =>
    document.querySelector(`${AUTOSUGGEST_SEL}.${SUGGEST_OPEN_CLASS}`) !== null;

const items = (): HTMLElement[] =>
    Array.from(document.querySelectorAll<HTMLElement>(ITEM_SEL))
        .filter(element => element.offsetParent !== null);

function setActive(input: HTMLInputElement, index: number): void {
    const list = items();
    list.forEach(element => element.classList.remove(ACTIVE_CLASS));

    if (index < 0 || list.length === 0) {
        currentIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
    }

    currentIndex = ((index % list.length) + list.length) % list.length;
    const active = list[currentIndex];
    if (!active.id) {
        active.id = 'suggest-opt-' + (++itemIdCounter);
    }
    active.classList.add(ACTIVE_CLASS);
    active.scrollIntoView({ block: 'nearest' });
    input.setAttribute('aria-activedescendant', active.id);
}

function init(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>(SEARCH_INPUT_SEL);
    inputs.forEach(input => {
        input.addEventListener('keydown', event => {
            if (!isOpen()) {
                return;
            }
            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const list = items();
                    if (list.length === 0) {
                        return;
                    }
                    const nextIndex = currentIndex < 0 ? 0 : currentIndex + 1;
                    setActive(input, nextIndex);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const list = items();
                    if (list.length === 0) {
                        return;
                    }
                    // currentIndex <= 0 wraps both the no-selection state (-1)
                    // and the first item (0) to the last item.
                    const previousIndex = currentIndex <= 0 ? list.length - 1 : currentIndex - 1;
                    setActive(input, previousIndex);
                    break;
                }
                case 'Enter':
                    if (currentIndex >= 0) {
                        const activeItem = items()[currentIndex];
                        if (activeItem) {
                            event.preventDefault();
                            activeItem.click();
                        }
                    }
                    break;
                case 'Escape':
                    closeSuggest();
                    input.focus();
                    setActive(input, -1);
                    break;
                default:
                    if (!NON_RESETTING_KEYS.has(event.key)) {
                        setActive(input, -1);
                    }
            }
        });
    });

    const autoSuggestRoots = document.querySelectorAll<HTMLElement>(AUTOSUGGEST_SEL);
    autoSuggestRoots.forEach(root => {
        const observer = new MutationObserver(() => {
            const focusedInput = document.activeElement instanceof HTMLInputElement
                ? document.activeElement
                : null;
            if (focusedInput && focusedInput.classList.contains('mainSearchInput')) {
                setActive(focusedInput, -1);
                return;
            }
            // Focus has left the input (e.g. after clicking an item). Clear the
            // highlight class on every navigable item directly so it isn't left
            // orphaned on a node that is about to be replaced by the live re-render.
            document
                .querySelectorAll<HTMLElement>(ITEM_SEL)
                .forEach(element => element.classList.remove(ACTIVE_CLASS));
            currentIndex = -1;
        });
        observer.observe(root, { childList: true, subtree: true });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
